import { Input } from "@/components/atoms/input";
import { Button } from "@/components/atoms/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";
import SearchableSelect, {
  SearchableOption,
} from "@/components/atoms/SearchableSelect";
import { Search, Plus, Filter, X } from "lucide-react";

interface SettingsFiltersProps {
  filters: any;
  brands: any[];
  roles: string[];
  onInviteClick: () => void;
}

export default function SettingsFilters({
  filters,
  brands,
  roles,
  onInviteClick,
}: SettingsFiltersProps) {
  // Convert brands to SearchableOption format
  const brandOptions: SearchableOption[] = brands.map((brand) => ({
    value: brand.id,
    label: brand.name,
  }));

  // Handle brand selection from SearchableSelect for multiple selection
  const handleBrandChange = (value: string | number | (string | number)[]) => {
    if (Array.isArray(value)) {
      filters.setSelectedBrand(value);
    } else {
      // If single value (like "all"), convert to array or handle specially
      if (value === "all") {
        filters.setSelectedBrand([]);
      } else {
        filters.setSelectedBrand([value]);
      }
    }
  };

  return (
    <>
      {/* Filters */}
      <div className="mb-6 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 w-full">
        {/* Search bar */}
        <div className="w-full order-1 xl:order-1">
          <Input
            type="text"
            placeholder="Search"
            value={filters.searchQuery}
            onChange={(e) => filters.setSearchQuery(e.target.value)}
            icon={<Search className="w-4 h-4" />}
            className="w-full bg-white"
          />
        </div>

        {/* Filters and button container */}
        <div className="w-full xl:w-auto flex flex-col sm:flex-row items-start sm:items-center justify-between xl:justify-end gap-4 order-2 xl:order-2">
          {/* Mobile Filter Button */}
          <div className="w-full sm:hidden">
            <Button
              onClick={filters.openFilterMenu}
              variant="outline"
              className="w-full bg-white text-gray-500 hover:bg-gray-50 text-sm px-3 py-2 h-10 justify-start"
              style={{ borderColor: "#e5e7eb" }}
            >
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              Filtros
            </Button>
          </div>

          {/* Desktop Filters */}
          <div className="hidden sm:flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Brand Dropdown with MULTIPLE SELECTION */}
            <SearchableSelect
              options={brandOptions}
              value={filters.selectedBrand}
              onValueChange={handleBrandChange}
              placeholder="Marcas"
              searchPlaceholder="Buscar marcas..."
              allOptionLabel="Todas las marcas"
              showAllOption={true}
              width="w-full sm:w-25 lg:w-40"
              noResultsText="No se encontraron marcas"
              searchPromptText="Escribe para buscar..."
              multiple={true}
              maxSelectedDisplay={2}
            />

            <Select
              value={filters.selectedRole}
              onValueChange={filters.setSelectedRole}
            >
              <SelectTrigger className="w-full sm:w-25 lg:w-40 text-xs lg:text-sm">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Rol</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Invite button */}
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg w-full sm:w-auto text-sm"
            onClick={onInviteClick}
          >
            <Plus className="w-4 h-4 mr-2" />
            Invitar
          </Button>
        </div>
      </div>

      {/* Mobile Filter Sidebar */}
      {filters.isFilterMenuOpen && (
        <div className="fixed inset-0 z-50 sm:hidden">
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${
              filters.isFilterMenuAnimating ? "bg-opacity-50" : "bg-opacity-0"
            }`}
            onClick={filters.closeFilterMenu}
          />

          {/* Sidebar */}
          <div
            className={`fixed right-0 top-0 h-full w-80 bg-white shadow-xl p-6 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
              filters.isFilterMenuAnimating
                ? "translate-x-0"
                : "translate-x-full"
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
              <button
                onClick={filters.closeFilterMenu}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Filters */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marcas
                </label>
                {/* Mobile Brand Dropdown with MULTIPLE SELECTION */}
                <SearchableSelect
                  options={brandOptions}
                  value={filters.selectedBrand}
                  onValueChange={handleBrandChange}
                  placeholder="Seleccionar marcas"
                  searchPlaceholder="Buscar marcas..."
                  allOptionLabel="Todas las marcas"
                  showAllOption={true}
                  width="w-full"
                  noResultsText="No se encontraron marcas"
                  searchPromptText="Escribe para buscar..."
                  multiple={true}
                  maxSelectedDisplay={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rol
                </label>
                <Select
                  value={filters.selectedRole}
                  onValueChange={filters.setSelectedRole}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-8">
              <Button
                onClick={filters.closeFilterMenu}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
